"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ProductRecommendation } from "@/lib/api";
import { createPaymentOrder, verifyPayment } from "@/lib/api/plans";
import {
  getGoogleAnalyticsClientId,
  trackAgentRevenueOfferClicked,
  trackRazorpayCheckoutOpened,
  trackReportCheckoutStarted,
} from "@/lib/analytics";
import type { ReportFollowupContext } from "@/lib/report-followup";
import { markCheckoutInFlight } from "@/lib/checkout-abandon-fallback";
import { getFirstTouchLanding } from "@/lib/landing-cluster";
import { buildIshvaramOneTimeRazorpayPolicy } from "@/lib/razorpay-checkout-policy";
import {
  completeSuccessfulPurchase,
  handleDismiss,
  razorpayOpenTracking,
  recordFailure,
} from "./PaidReportCTA.checkout";
import {
  loadRazorpayScript,
  normalizeContactEmail,
  normalizeContactPhone,
} from "./PaidReportCTA.payment";
import type { PaidReportCTAProps } from "./PaidReportCTA.types";

export interface RemedyKitState {
  href: string;
  products: ProductRecommendation[];
  orderId: string;
}

export interface UsePaidReportCTAReturn {
  paying: boolean;
  error: string | null;
  downloadUrl: string | null;
  followup: ReportFollowupContext | null;
  checkoutAbandoned: boolean;
  recoveryHref: string | null;
  checkoutEmail: string;
  checkoutPhone: string;
  remedyKit: RemedyKitState | null;
  setCheckoutEmail: (v: string) => void;
  setCheckoutPhone: (v: string) => void;
  handleBuy: () => Promise<void>;
}

type HookProps = Pick<
  PaidReportCTAProps,
  | "planId"
  | "priceInr"
  | "reportInputs"
  | "readingId"
  | "sessionId"
  | "prefill"
  | "remedyUpsellProducts"
  | "source"
  | "analyticsContext"
  | "onAgentMemoryEvent"
  | "autoOpen"
> &
  Partial<Pick<PaidReportCTAProps, "headline" | "subhead">>;

export type PaymentAttribution = ReturnType<typeof buildPaymentAttribution>;
export type PaymentOrder = Awaited<ReturnType<typeof createPaymentOrder>>;
export type RazorpayResponse = {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};
export type OrderLike = Pick<PaymentOrder, "order_id">;
export type CloseReason = "dismissed" | "payment_success" | "payment_failed" | "open_failed";

export interface HookState {
  setPaying: (value: boolean) => void;
  setError: (value: string | null) => void;
  setDownloadUrl: (value: string | null) => void;
  setFollowup: (value: ReportFollowupContext | null) => void;
  setCheckoutAbandoned: (value: boolean) => void;
  setRecoveryHref: (value: string | null) => void;
  setRemedyKit: (value: RemedyKitState | null) => void;
}

export interface CheckoutContext {
  props: HookProps;
  state: HookState;
  sourcePath?: string;
  attribution: PaymentAttribution;
  customerEmail: string;
  customerPhone: string;
  readingId: string;
  sessionId: string;
  recoveryUrl: string;
  /** Meta CAPI dedup: same event_id used for browser pixel + backend CAPI fire. */
  event_id: string;
}

export function usePaidReportCTA(props: HookProps): UsePaidReportCTAReturn {
  const { prefill } = props;
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [followup, setFollowup] = useState<ReportFollowupContext | null>(null);
  const [checkoutAbandoned, setCheckoutAbandoned] = useState(false);
  const [recoveryHref, setRecoveryHref] = useState<string | null>(null);
  const [checkoutEmail, setCheckoutEmail] = useState(prefill?.email ?? "");
  const [checkoutPhone, setCheckoutPhone] = useState(prefill?.contact ?? "");
  const [remedyKit, setRemedyKit] = useState<RemedyKitState | null>(null);
  const state = useMemo(
    () => ({
      setPaying,
      setError,
      setDownloadUrl,
      setFollowup,
      setCheckoutAbandoned,
      setRecoveryHref,
      setRemedyKit,
    }),
    []
  );
  const handleBuy = useCallback(
    () => startCheckout(props, state, checkoutEmail, checkoutPhone),
    [props, state, checkoutEmail, checkoutPhone]
  );

  // Auto-open: fires the checkout once on mount when the agent dispatcher
  // requests it (recover_report_checkout / show_report_checkout tool calls).
  // The ref guard prevents re-triggering on re-renders.
  const autoOpenFired = useRef(false);
  useEffect(() => {
    if (!props.autoOpen || autoOpenFired.current) return;
    autoOpenFired.current = true;
    void handleBuy();
  }, [props.autoOpen, handleBuy]);

  return {
    paying,
    error,
    downloadUrl,
    followup,
    checkoutAbandoned,
    recoveryHref,
    checkoutEmail,
    checkoutPhone,
    remedyKit,
    setCheckoutEmail,
    setCheckoutPhone,
    handleBuy,
  };
}

async function startCheckout(
  props: HookProps,
  state: HookState,
  checkoutEmail: string,
  checkoutPhone: string
) {
  // Meta CAPI dedup: same event_id used for browser pixel + backend CAPI fire.
  const event_id = crypto.randomUUID();
  const ctx = buildCheckoutContext(props, state, checkoutEmail, checkoutPhone, event_id);
  state.setError(null);
  state.setCheckoutAbandoned(false);
  state.setPaying(true);
  try {
    // Require AT LEAST ONE of email or phone (not both — that's too much
    // friction and abandonment goes up). Per lesson_razorpay_chat_prefill:
    // the void@razorpay.com bug happens when prefill is EMPTY — having
    // either email or phone in prefill keeps the customer reachable for
    // support + remarketing. normalizeContactEmail already strips
    // void@razorpay.com, so an empty customerEmail means no real email.
    if (!ctx.customerEmail && !ctx.customerPhone) return missingContact(state);
    if (!reportInputsAreComplete(props.planId, props.reportInputs)) {
      return invalidReportInputs(state, props.planId);
    }
    const order = await createCheckoutOrder(ctx);
    // Fire `report_checkout_started` AFTER the email gate + order creation
    // succeed, immediately before the SDK opens. Pre-fix it fired before
    // the gate, producing 915 events/wk vs 112 actual Razorpay attempts.
    trackCheckoutStarted(ctx);
    await openRazorpayCheckout(ctx, order);
  } catch (error) {
    state.setError(error instanceof Error ? error.message : "Could not start checkout.");
    state.setPaying(false);
  }
}

function buildCheckoutContext(
  props: HookProps,
  state: HookState,
  checkoutEmail: string,
  checkoutPhone: string,
  event_id: string
): CheckoutContext {
  const sourcePath = currentSourcePath(props);
  return {
    props,
    state,
    sourcePath,
    attribution: buildPaymentAttribution(props, sourcePath),
    customerEmail: normalizeContactEmail(checkoutEmail || props.prefill?.email),
    customerPhone: normalizeContactPhone(checkoutPhone || props.prefill?.contact),
    readingId: resolvedString(
      props.readingId,
      props.reportInputs.reading_id,
      props.reportInputs.readingId
    ),
    sessionId: resolvedString(
      props.sessionId,
      props.reportInputs.session_id,
      props.reportInputs.sessionId
    ),
    recoveryUrl: reportCheckoutRecoveryUrl(props, sourcePath),
    event_id,
  };
}

function currentSourcePath({ analyticsContext }: HookProps): string | undefined {
  if (analyticsContext?.sourcePath) return analyticsContext.sourcePath;
  return typeof window !== "undefined" ? window.location.pathname || undefined : undefined;
}

function firstTouchAttribution() {
  const firstTouch = getFirstTouchLanding();
  return {
    landing_cluster: firstTouch.cluster ?? undefined,
    landing_path: firstTouch.path ?? undefined,
  };
}

function buildPaymentAttribution({ source, analyticsContext }: HookProps, sourcePath?: string) {
  return {
    source,
    placement: analyticsContext?.placement,
    checkout_channel: analyticsContext?.checkoutChannel ?? "ishvaram_report",
    commerce_system: analyticsContext?.commerceSystem ?? "ishvaram",
    intent: analyticsContext?.intent,
    source_path: sourcePath,
    ...firstTouchAttribution(),
    customer_problem: analyticsContext?.customerProblem,
    agent_trace_id: analyticsContext?.agentTraceId,
    ga_client_id: getGoogleAnalyticsClientId(),
  };
}

function missingContact(state: HookState) {
  // Either is fine — match the form copy. Pre-fix this said "email address"
  // only, which made users who only had a phone think the page was broken.
  state.setError("Enter your email or mobile number to receive the report.");
  state.setPaying(false);
}

function invalidReportInputs(state: HookState, planId: string) {
  state.setError(
    planId === "marriage_report"
      ? "Marriage report needs both partners' birth details. Please use Kundli Matching or Gun Milan."
      : "Birth details are incomplete. Please regenerate the chart before buying the report."
  );
  state.setPaying(false);
}

function reportInputsAreComplete(planId: string, inputs: Record<string, unknown>): boolean {
  if (planId === "marriage_report") {
    return hasPerson(inputs.person_a) && hasPerson(inputs.person_b);
  }
  return Boolean(inputs.name && inputs.birth_date && inputs.birth_place);
}

function hasPerson(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const person = value as Record<string, unknown>;
  return Boolean(person.name && person.birth_date && person.birth_place);
}

function trackCheckoutStarted({ props, attribution }: CheckoutContext) {
  props.onAgentMemoryEvent?.("checkout_started", { priceInr: props.priceInr });
  trackAgentRevenueOfferClicked(agentOfferClickedPayload({ props, attribution }));
  trackReportCheckoutStarted(reportCheckoutStartedPayload({ props, attribution }));
}

function agentOfferClickedPayload({
  props,
  attribution,
}: Pick<CheckoutContext, "props" | "attribution">) {
  const { planId, analyticsContext } = props;
  return {
    source_path: attribution.source_path,
    intent: attribution.intent,
    offer_type: "report",
    plan_id: planId,
    placement: attribution.placement,
    checkout_channel: attribution.checkout_channel,
    commerce_system: attribution.commerce_system,
    route_rank_score: analyticsContext?.routeRankScore,
    route_rank_reason: analyticsContext?.routeRankReason,
    ranked_offer_order: analyticsContext?.rankedOfferOrder,
    agent_trace_id: analyticsContext?.agentTraceId,
  };
}

function reportCheckoutStartedPayload({
  props,
  attribution,
}: Pick<CheckoutContext, "props" | "attribution">) {
  const { planId, priceInr, analyticsContext } = props;
  return {
    plan_id: planId,
    price_inr: priceInr,
    ...attribution,
    urgency: analyticsContext?.urgency,
    confidence: analyticsContext?.confidence,
    route_rank_score: analyticsContext?.routeRankScore,
    route_rank_reason: analyticsContext?.routeRankReason,
    ranked_offer_order: analyticsContext?.rankedOfferOrder,
  };
}

function createCheckoutOrder({
  props,
  attribution,
  customerEmail,
  customerPhone,
  readingId,
  sessionId,
  event_id,
}: CheckoutContext) {
  return createPaymentOrder({
    plan_id: props.planId,
    plan_type: "report",
    reading_id: readingId || undefined,
    session_id: sessionId || undefined,
    email: customerEmail,
    phone: customerPhone,
    ...attribution,
    report_inputs: props.reportInputs,
    event_id,
  });
}

async function openRazorpayCheckout(ctx: CheckoutContext, order: PaymentOrder) {
  await loadRazorpayScript();
  if (!window.Razorpay) throw new Error("Razorpay unavailable");
  const rzp = new window.Razorpay(razorpayOptions(ctx, order));
  rzp.on?.("payment.failed", (response) => recordFailure(ctx, order, response));
  trackRazorpayCheckoutOpened(razorpayOpenTracking(ctx, order));
  // Browser-unload failsafe: if the user tabs away or closes the
  // browser without the modal `ondismiss` firing, this listener catches
  // it and emits the same abandon event handleDismiss would have. See
  // Phase 5b notes in checkout-abandon-fallback.ts.
  markCheckoutInFlight({
    orderId: order.order_id,
    fireAbandon: () => handleDismiss(ctx, order),
  });
  rzp.open();
}

function razorpayOptions(ctx: CheckoutContext, order: PaymentOrder): Record<string, unknown> {
  return {
    key: order.key_id,
    amount: order.amount,
    currency: order.currency,
    name: "Ishvaram Jyotish",
    description: order.plan_name,
    order_id: order.order_id,
    ...buildIshvaramOneTimeRazorpayPolicy(),
    prefill: razorpayPrefill(ctx),
    notes: razorpayNotes(ctx),
    handler: (response: RazorpayResponse) => handlePaymentSuccess(ctx, response),
    modal: { ondismiss: () => handleDismiss(ctx, order) },
  };
}

function razorpayPrefill({ props, customerEmail, customerPhone }: CheckoutContext) {
  return {
    ...(props.prefill ?? {}),
    ...(customerEmail ? { email: customerEmail } : {}),
    ...(customerPhone ? { contact: customerPhone } : {}),
  };
}

function razorpayNotes({ props, attribution, readingId, sessionId }: CheckoutContext) {
  return {
    plan_id: props.planId,
    plan_type: "report",
    reading_id: readingId,
    session_id: sessionId,
    source: props.source ?? "",
    placement: attribution.placement ?? "",
    checkout_channel: attribution.checkout_channel,
    intent: attribution.intent ?? "",
    source_path: attribution.source_path ?? "",
    customer_problem: attribution.customer_problem ?? "",
    urgency: props.analyticsContext?.urgency ?? "",
  };
}

async function handlePaymentSuccess(ctx: CheckoutContext, response: RazorpayResponse) {
  try {
    const result = await verifyPayment(verifyPayload(ctx, response));
    if (!result.success || !result.download_url) return reportGenerationFailed(ctx);
    completeSuccessfulPurchase(ctx, response, result.download_url);
  } catch (error) {
    ctx.state.setError(
      error instanceof Error ? error.message : "Could not verify payment. Try again."
    );
  } finally {
    ctx.state.setPaying(false);
  }
}

function verifyPayload(
  {
    props,
    attribution,
    customerEmail,
    customerPhone,
    readingId,
    sessionId,
    event_id,
  }: CheckoutContext,
  response: RazorpayResponse
) {
  return {
    razorpay_order_id: String(response.razorpay_order_id),
    razorpay_payment_id: String(response.razorpay_payment_id),
    razorpay_signature: String(response.razorpay_signature),
    plan_id: props.planId,
    plan_type: "report",
    reading_id: readingId || undefined,
    session_id: sessionId || undefined,
    email: customerEmail,
    // Thread phone so the verify path can drive WhatsApp PDF delivery
    // server-side when the customer provides a valid +91 mobile.
    phone: customerPhone,
    ...attribution,
    report_inputs: props.reportInputs,
    event_id,
  };
}

function reportGenerationFailed({ state }: CheckoutContext) {
  state.setError(
    "Payment verified but report generation failed. Email support@ishvaram.com with your order ID."
  );
}

function resolvedString(...values: unknown[]): string {
  const value = values.find((candidate) => typeof candidate === "string" && candidate.trim());
  return typeof value === "string" ? value.trim() : "";
}

function reportCheckoutRecoveryUrl(props: HookProps, sourcePath?: string): string {
  const readingId = resolvedString(
    props.readingId,
    props.reportInputs.reading_id,
    props.reportInputs.readingId
  );
  const params = new URLSearchParams({
    src: "checkout_recovery",
    checkout_recovery: "1",
    plan_id: props.planId,
    plan_type: "report",
    next_report: props.planId,
  });
  if (readingId) {
    return `/chat/${encodeURIComponent(readingId)}?${params.toString()}`;
  }
  const path = sourcePath || "/free-kundali";
  return `${path}${path.includes("?") ? "&" : "?"}${params.toString()}`;
}
